// src/components/user/express/Step1Pickup.jsx
import React, { useEffect, useRef } from 'react';
import { MapPin, Navigation } from 'lucide-react';

export function Step1Pickup({
  savedAddresses,
  pickupAddress, setPickupAddress,
  setPickupLat, setPickupLng,
}) {
  const inputRef    = useRef(null);
  const mapRef      = useRef(null);
  const mapInstance = useRef(null);
  const markerRef   = useRef(null);

  // Init Google Map
  useEffect(() => {
    if (!window.google || mapInstance.current) return;
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: -12.0464, lng: -77.0428 }, // Lima center
      zoom: 13,
      disableDefaultUI: true,
      zoomControl: true,
      styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
    });
    mapInstance.current = map;

    // Allow click-to-pin on map
    map.addListener('click', (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      placeMarker(lat, lng);
      reverseGeocode(lat, lng);
    });
  }, []);

  // Init Places Autocomplete
  useEffect(() => {
    if (!window.google || !inputRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'pe' },
      fields: ['formatted_address', 'geometry'],
    });
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place.geometry) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setPickupAddress(place.formatted_address);
      setPickupLat(lat);
      setPickupLng(lng);
      placeMarker(lat, lng);
      mapInstance.current?.setCenter({ lat, lng });
      mapInstance.current?.setZoom(16);
    });
  }, []);

  function placeMarker(lat, lng) {
    if (markerRef.current) markerRef.current.setMap(null);
    markerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstance.current,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#B1336E',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
      },
    });
    setPickupLat(lat);
    setPickupLng(lng);
  }

  function reverseGeocode(lat, lng) {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setPickupAddress(results[0].formatted_address);
        if (inputRef.current) inputRef.current.value = results[0].formatted_address;
      }
    });
  }

  function handleSelectSaved(addr) {
    setPickupAddress(addr.address);
    setPickupLat(addr.latitude ?? addr.lat ?? null);
    setPickupLng(addr.longitude ?? addr.lng ?? null);
    if (inputRef.current) inputRef.current.value = addr.address;
    if (addr.latitude && mapInstance.current) {
      const lat = addr.latitude ?? addr.lat;
      const lng = addr.longitude ?? addr.lng;
      placeMarker(lat, lng);
      mapInstance.current.setCenter({ lat, lng });
      mapInstance.current.setZoom(16);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-extrabold text-slate-800 font-montserrat mb-1">
          ¿Dónde recogemos el paquete?
        </h2>
        <p className="text-sm text-slate-500 font-montserrat">
          Selecciona una dirección guardada o escribe una nueva
        </p>
      </div>

      {/* Saved address chips */}
      {savedAddresses.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {savedAddresses.map((addr, idx) => (
            <button
              key={idx}
              onClick={() => handleSelectSaved(addr)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold font-montserrat border transition-all
                ${pickupAddress === addr.address
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-brand-primary hover:text-brand-primary'
                }`}
            >
              <MapPin className="w-3 h-3" />
              {addr.label || addr.address?.substring(0, 28) + '…'}
            </button>
          ))}
        </div>
      )}

      {/* Autocomplete input */}
      <div className="relative">
        <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-primary" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Buscar dirección de recojo…"
          defaultValue={pickupAddress}
          className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-primary outline-none text-sm font-montserrat transition-colors"
        />
      </div>

      {/* Map */}
      <div
        ref={mapRef}
        className="w-full h-52 rounded-xl overflow-hidden border-2 border-slate-200"
      />

      {pickupAddress && (
        <div className="flex items-start gap-2 bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-3 py-2">
          <MapPin className="w-4 h-4 text-brand-primary mt-0.5 flex-shrink-0" />
          <p className="text-xs text-slate-700 font-montserrat">{pickupAddress}</p>
        </div>
      )}
    </div>
  );
}